import { Building2, Check, ChevronDown, CloudOff, Coins, Image as ImageIcon, LayoutGrid, Plus, ScrollText, Settings, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTool } from "@/api/hooks";
import { cn } from "@/lib/cn";
import { compact, money } from "@/lib/format";
import { useAuth } from "@/state/auth";
import { brandPath, lastBrand, rememberBrand, useBrands, useRouteBrandId } from "@/state/brand";
import { BrandMark } from "../ui/BrandMark";
import { SearchInput } from "../ui/Form";
import { MenuItem, MenuLabel, MenuSeparator, PopoverPanel, usePopover } from "../ui/Overlay";
import { Spinner } from "../ui/Feedback";

function Crumb({ label, icon, open, onClick, anchor, muted }: { label: React.ReactNode; icon?: React.ReactNode; open: boolean; onClick: () => void; anchor: (el: HTMLButtonElement | null) => void; muted?: boolean }) {
  return (
    <button
      type="button"
      ref={anchor}
      onClick={onClick}
      aria-expanded={open}
      className={cn(
        "inline-flex h-9 max-w-[240px] items-center gap-2 rounded-xl border border-transparent px-3 text-[14px] font-medium transition-colors hover:border-line hover:bg-surface",
        open && "border-line bg-surface",
        muted && "text-muted",
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
      <ChevronDown className={cn("size-4 shrink-0 text-muted transition-transform", open && "rotate-180")} />
    </button>
  );
}

function WorkspaceSwitcher() {
  const p = usePopover();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const settings = useTool<Record<string, unknown>>("settings.get", {}, { staleTime: 5 * 60_000 });
  const s = (settings.data && typeof settings.data === "object" ? ((settings.data as Record<string, unknown>).settings ?? settings.data) : {}) as Record<string, unknown>;
  const pick = (...keys: string[]) => keys.map((k) => s[k]).find((v): v is string => typeof v === "string" && v.trim() !== "");
  const name = pick("org.name", "orgName", "workspace.name", "workspaceName") ?? "Mimic Studio";
  const go = (to: string) => {
    nav(to);
    p.close();
  };
  return (
    <>
      <Crumb label={name} open={p.open} onClick={p.toggle} anchor={(el) => (p.anchorRef.current = el)} />
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} className="w-60 p-1.5">
        <MenuLabel>Workspace</MenuLabel>
        <MenuItem icon={<LayoutGrid className="size-4" />} onClick={() => go("/all")}>
          All brands overview
        </MenuItem>
        <MenuItem icon={<Building2 className="size-4" />} onClick={() => go("/brands")}>
          Brands
        </MenuItem>
        <MenuItem icon={<Users className="size-4" />} onClick={() => go("/settings/team")}>
          Team
        </MenuItem>
        <MenuItem icon={<Settings className="size-4" />} onClick={() => go("/settings/general")}>
          Settings
        </MenuItem>
        {isAdmin && (
          <MenuItem icon={<ScrollText className="size-4" />} onClick={() => go("/settings/audit")}>
            Audit log
          </MenuItem>
        )}
      </PopoverPanel>
    </>
  );
}

/** Pages that exist per brand — switching brand keeps you on the same page. */
const BRAND_PAGES = ["home", "decisions", "check", "monitor", "performance", "creations", "studio", "inspiration", "connections", "scheduler", "settings", "assets"];

function BrandSwitcher() {
  const p = usePopover();
  const nav = useNavigate();
  const loc = useLocation();
  const routeBrand = useRouteBrandId();
  const { brands, isLoading, error } = useBrands();
  const [q, setQ] = useState("");
  const allMode = loc.pathname === "/all" || loc.pathname === "/decisions";
  // Global pages (chat, settings) show the remembered brand — the rail's links point there too.
  const effective = routeBrand ?? (allMode ? null : lastBrand());
  const current = brands.find((b) => b.id === effective || b.slug === effective);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? brands.filter((b) => b.name.toLowerCase().includes(s)) : brands;
  }, [brands, q]);
  const page = (() => {
    const m = loc.pathname.match(/^\/b\/[^/]+\/([^/]+)/);
    return m && BRAND_PAGES.includes(m[1]!) ? m[1]! : "home";
  })();
  const pick = (id: string) => {
    rememberBrand(id);
    nav(brandPath(id, page));
    p.close();
    setQ("");
  };
  const label = allMode ? "All brands" : (current?.name ?? (effective ? "Brand" : "Select brand"));
  return (
    <>
      <Crumb
        label={label}
        muted={!current && !allMode}
        icon={
          current ? (
            <BrandMark name={current.name} logoUrl={current.logoUrl} className="size-5 rounded-md" textClassName="text-[11px]" />
          ) : allMode ? (
            <LayoutGrid className="size-4 text-muted" />
          ) : undefined
        }
        open={p.open}
        onClick={p.toggle}
        anchor={(el) => (p.anchorRef.current = el)}
      />
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} className="w-72 p-1.5">
        <div className="p-1">
          <SearchInput value={q} onChange={setQ} placeholder="Find a brand…" className="[&_input]:h-8 [&_input]:text-[12.5px]" />
        </div>
        <MenuItem
          icon={<LayoutGrid className="size-4" />}
          right={allMode ? <Check className="size-4 text-lavender" /> : undefined}
          onClick={() => {
            nav("/all");
            p.close();
          }}
          description="Multi-brand overview"
        >
          All brands
        </MenuItem>
        <MenuSeparator />
        <MenuLabel>Brands</MenuLabel>
        <div className="max-h-72 overflow-y-auto scrollbar-thin">
          {isLoading ? (
            <div className="flex items-center gap-2 px-2.5 py-2 text-xs text-muted">
              <Spinner size={12} /> Loading brands…
            </div>
          ) : error ? (
            <div className="px-2.5 py-2 text-xs text-subtle">Brands unavailable</div>
          ) : filtered.length === 0 ? (
            <div className="px-2.5 py-2 text-xs text-subtle">{q ? "No match" : "No brands yet"}</div>
          ) : (
            filtered.map((b) => (
              <MenuItem
                key={b.id}
                icon={<BrandMark name={b.name} logoUrl={b.logoUrl} className="size-4 rounded" textClassName="text-[9px]" />}
                right={b.id === current?.id && !allMode ? <Check className="size-4 text-lavender" /> : b.status && b.status !== "active" ? <span className="text-[10.5px] text-amber">{b.status}</span> : undefined}
                onClick={() => pick(b.id)}
              >
                {b.name}
              </MenuItem>
            ))
          )}
        </div>
        <MenuSeparator />
        {current && (
          <>
            <MenuItem
              icon={<Settings className="size-4" />}
              onClick={() => {
                nav(brandPath(current.id, "settings"));
                p.close();
              }}
            >
              Brand settings
            </MenuItem>
            <MenuItem
              icon={<ImageIcon className="size-4" />}
              onClick={() => {
                nav(brandPath(current.id, "assets"));
                p.close();
              }}
            >
              Brand assets
            </MenuItem>
          </>
        )}
        <MenuItem
          icon={<Plus className="size-4 text-link" />}
          className="text-link"
          onClick={() => {
            nav("/brands/new");
            p.close();
          }}
        >
          New brand
        </MenuItem>
      </PopoverPanel>
    </>
  );
}

interface UsageSummary {
  muapi?: { credits?: number | null; spendMonthUsd?: number | null; generationsMonth?: number | null };
  scrapecreators?: { credits?: number | null; requestsMonth?: number | null };
  llm?: { costMonthUsd?: number | null; tokensMonth?: number | null };
  month?: string;
  providers?: { provider: string; credits?: number | null; costMonthUsd?: number | null; units?: number | null }[];
}

function UsagePill() {
  const p = usePopover();
  const q = useTool<UsageSummary>("usage.summary", {}, { staleTime: 5 * 60_000, refetchInterval: 5 * 60_000 });
  const u = q.data ?? {};
  const credits = u.muapi?.credits;
  const spend = u.muapi?.spendMonthUsd;
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        className={cn("inline-flex h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-[13px] transition-colors hover:border-line-strong", p.open && "border-line-strong")}
        aria-label="Usage"
      >
        <Coins className="size-4 text-amber" strokeWidth={1.75} />
        {q.isLoading ? (
          <span className="text-muted">Usage…</span>
        ) : q.error ? (
          <span className="text-muted">Usage</span>
        ) : (
          <>
            <span className="font-semibold tnum">{credits !== undefined && credits !== null ? compact(credits) : "—"}</span>
            <span className="hidden text-muted sm:inline">credits</span>
            <span className="hidden h-4 w-px bg-line-strong sm:inline-block" />
            <span className="hidden text-muted tnum sm:inline">{money(spend ?? null, "USD", { compact: true })} this month</span>
          </>
        )}
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} align="end" className="w-72 p-3">
        <div className="mb-2 text-[12px] font-semibold">Provider usage{u.month ? ` · ${u.month}` : " · this month"}</div>
        {q.error ? (
          <div className="text-xs text-muted">Usage isn't available from the server yet.</div>
        ) : (
          <div className="flex flex-col divide-y divide-line text-[12.5px]">
            <UsageRow label="MuAPI credits" value={compact(u.muapi?.credits ?? null)} />
            <UsageRow label="Generation spend" value={money(u.muapi?.spendMonthUsd ?? null)} />
            {u.muapi?.generationsMonth !== undefined && <UsageRow label="Generations" value={compact(u.muapi?.generationsMonth ?? null)} />}
            <UsageRow label="ScrapeCreators credits" value={compact(u.scrapecreators?.credits ?? null)} />
            {u.llm && <UsageRow label="LLM cost" value={money(u.llm.costMonthUsd ?? null)} />}
            {(u.providers ?? []).map((pr) => (
              <UsageRow key={pr.provider} label={pr.provider} value={pr.costMonthUsd !== undefined ? money(pr.costMonthUsd) : compact(pr.credits ?? pr.units ?? null)} />
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-subtle">Internal tool — balances are read from provider APIs and the usage ledger.</p>
      </PopoverPanel>
    </>
  );
}

function UsageRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-muted">{label}</span>
      <span className="font-semibold tnum">{value}</span>
    </div>
  );
}

export function TopBar() {
  const { offline, refetch } = useAuth();
  return (
    <div className="sticky top-0 z-30 bg-canvas/85 backdrop-blur-md">
      {offline && (
        <div className="flex items-center justify-center gap-2 bg-danger-soft px-4 py-1.5 text-[12.5px] text-danger">
          <CloudOff className="size-3.5" /> Can't reach the Mimic server — showing offline states.
          <button type="button" onClick={refetch} className="font-medium underline underline-offset-2">
            Retry
          </button>
        </div>
      )}
      <div className="flex h-14 items-center justify-between gap-3 px-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-0.5">
          <span className="hidden sm:inline-flex">
            <WorkspaceSwitcher />
          </span>
          <span className="hidden px-1 text-muted sm:inline">/</span>
          <BrandSwitcher />
        </div>
        <UsagePill />
      </div>
    </div>
  );
}
